import React, { useEffect } from "react";
import { Modal, Form, Input, Button, message, Row, Col, Typography } from "antd";
import { SaveOutlined, CopyOutlined } from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { configService } from "../services";
import { InboundApiConfig, MedicalFacility } from "../types";
import { envConfig } from "../config/env";
import "./InboundApiConfigModal.css";
import RichTextEditorTiny from "./TinyMCEEditor";

const { TextArea } = Input;
const { Title, Text } = Typography;

interface InboundApiConfigModalProps {
  visible: boolean;
  facility: MedicalFacility | null;
  onClose: () => void;
}

const InboundApiConfigModal: React.FC<InboundApiConfigModalProps> = ({
  visible,
  facility,
  onClose,
}) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const facilityId = facility?.id ?? 0;

  const { data: inboundApiConfig } = useQuery({
    queryKey: ["admin-inbound-api-config", facilityId],
    queryFn: () => configService.getAdminInboundApiConfig(facilityId),
    enabled: visible && facilityId > 0,
  });

  const saveMutation = useMutation({
    mutationFn: (values: InboundApiConfig) => {
      return configService.saveAdminInboundApiConfig(facilityId, values);
    },
    onSuccess: () => {
      message.success("Lưu cấu hình kết nối thành công!");
      queryClient.invalidateQueries({
        queryKey: ["admin-inbound-api-config", facilityId],
      });
    },
    onError: () => {
      message.error("Lưu cấu hình kết nối thất bại!");
    },
  });

  useEffect(() => {
    if (visible && inboundApiConfig?.data?.data) {
      const d = inboundApiConfig.data.data;
    }
  }, [visible, form, inboundApiConfig]);

  const onFinish = (values: any) => {
    saveMutation.mutate(values);
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`Đã sao chép ${label}`);
    });
  };

  const apiServerUrl = inboundApiConfig?.data?.data?.allowed_hosts || "";
  const displayUsername = inboundApiConfig?.data?.data?.username || "";
  const displayPassword = inboundApiConfig?.data?.data?.password || "";

  return (
    <Modal
      title={facility?.name || "Cấu hình kết nối"}
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={900}
      destroyOnClose
    >
      <div className="inbound-api-config-modal-content">
        <Row gutter={32}>
          <Col span={12}>
            <Form form={form} layout="vertical" onFinish={onFinish}>
              <Form.Item
                name="allowed_hosts"
                label="API Server"
                rules={[
                  { required: true, message: "Vui lòng nhập API Server!" },
                ]}
              >
                <Input placeholder="Nhập API Server" />
              </Form.Item>

              <Form.Item
                name="username"
                label="Tài khoản"
                rules={[
                  { required: true, message: "Vui lòng nhập tài khoản!" },
                ]}
              >
                <Input placeholder="Nhập tài khoản" />
              </Form.Item>

              <Form.Item
                name="password"
                label="Mật khẩu"
                rules={[
                  { required: true, message: "Vui lòng nhập mật khẩu!" },
                ]}
              >
                <Input.Password placeholder="Nhập mật khẩu" />
              </Form.Item>

              <Form.Item name="notes" label="Ghi chú">
                <RichTextEditorTiny />
              </Form.Item>

              <div className="form-actions">
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={() => form.submit()}
                  loading={saveMutation.isPending}
                >
                  Lưu thông tin
                </Button>
              </div>
            </Form>
          </Col>

          <Col span={12}>
            <div className="api-info-section">
              <Title level={5} className="api-info-title">
                Thông tin API
              </Title>

              <div className="api-info-item">
                <Text className="api-info-label">API Server</Text>
                <div className="api-info-value-row">
                  <Text className="api-info-value">{apiServerUrl}</Text>
                  <Button
                    type="link"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(apiServerUrl, "API Server")}
                    className="copy-btn"
                  >
                    Sao chép
                  </Button>
                </div>
              </div>

              <div className="api-info-item">
                <Text className="api-info-label">Tài khoản</Text>
                <div className="api-info-value-row">
                  <Text className="api-info-value">{displayUsername}</Text>
                  <Button
                    type="link"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(displayUsername, "Tài khoản")}
                    className="copy-btn"
                  >
                    Sao chép
                  </Button>
                </div>
              </div>

              <div className="api-info-item">
                <Text className="api-info-label">Mật khẩu</Text>
                <div className="api-info-value-row">
                  <Text className="api-info-value">{displayPassword}</Text>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default InboundApiConfigModal;
